import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useDebounce } from '../hooks/useDebounce';
import axios from 'axios';
import { useHash } from '../hooks/useHash';
import {stringify} from 'csv-stringify/browser/esm/sync';
import mapboxgl from 'mapbox-gl';
import { MAPBOX_API_KEY } from '../utils/mapbox-api-key';

export enum Tab {
    VENUES,
    ROUTE,
    ABOUT
}
export type AppFeature = GeoJSON.Feature<GeoJSON.Point, {
    title: string, address: string, imageUrl?: string, _path: string, venue_type: string
    pax: string,
    capacity_category: string,
    region?: string,
    room_name: string,
    venue_phone: string,
    venue_email: string,
    website: string,
    facebook: string,
    instagram: string,
    booking_name: string,
    booking_email: string,
    booking_phone: string,
    engineer: string,
    accessibility_audience: string,
    accessibility_artist: string,
}>;
export interface MapViewport {
    center: mapboxgl.LngLatLike
    zoom: number
    bounds: mapboxgl.LngLatBounds
}

export interface FilterItem {
    label: string;
    values: string[];
}

export interface FilterType {
    // venueType: FilterType[],
    // lighting: FilterType[],
    // accessibility: boolean,

    venueType: FilterType[],
    capacityCategory: FilterType[],
    accessibilityAudience: boolean,
    accessibilityArtist: boolean
}

export const FILTER_CONFIG: {[k: string]: {
    type: string,
    label: string,
    description?: string,
    keys: string[],
    selectValues?: FilterItem[],
    values?: string[]
}} = {
    venueType: {
        type: 'multiselect',
        label: 'Venue types',
        keys: ['venue_type'],
        selectValues: [{
            label: 'Cafe/Restaurant',
            values: ['Cafe/Restaurant']
        },
        {
            label: 'Club',
            values: ['Club']
        },
        {
            label: 'Community space/hall',
            values: ['Community space/hall']
        },
        {
            label: 'Dedicated live music venue',
            values: ['Dedicated live music venue']
        },
        {
            label: 'Festival',
            values: ['Festival']
        },
        {
            label: 'Pub/Hotel/Bar',
            values: ['Pub/Hotel/Bar']
        },
        {
            label: 'Theatre or Performing art space',
            values: ['Theatre or Performing art space']
        }]
    },
    capacityCategory: {
        type: 'multiselect',
        label: 'Capacity',
        keys: ['capacity_category'],
        selectValues: [{
            label: '< 120',
            values: ['< 120']
        },
        {
            label: '120 - 249',
            values: ['120 - 249']
        },
        {
            label: '250 - 499',
            values: ['250 - 499']
        },
        {
            label: '500 - 999',
            values: ['500 - 999']
        },
        {
            label: '1,000 +',
            values: ['1,000 +']
        },
        {
            label: 'Unknown',
            values: ['Unknown']
        }]
    },
    accessibilityAudience: {
        type: 'checkbox',
        label: 'Audience',
        description: 'Wheelchair accessible',
        keys: ['accessibility_audience'],
        values: ['Yes'],
    },
    accessibilityArtist: {
        type: 'checkbox',
        label: 'Artist',
        description: 'Wheelchair accessible',
        keys: ['accessibility_artist'],
        values: ['Yes'],
    }
}
export interface AppContextProps {
    tab: Tab,
    setTab: (tab: Tab) => void,

    mobileToggle: boolean;
    setMobileToggle: (s: boolean) => void;

    mapViewport?: MapViewport,
    setMapViewport: (viewport: MapViewport) => void,

    venues: AppFeature[],
    filteredVenues: AppFeature[],

    filters: FilterType,
    setFilters: (filter: FilterType) => void,

    selectedFilters: FilterType,
    setSelectedFilters: (filter: FilterType) => void,

    showFilters: boolean,
    setShowFilters: (showFilters: boolean) => void,

    filterCounter: number,
    setFilterCounter: (filterCounter: number) => void,

    isFilterMapMove: boolean,
    setIsFilterMapMove: (tf: boolean) => void,

    selectedVenue: AppFeature|null,
    setSelectedVenue: (venue: AppFeature|null) => void,

    venueOptions: AppFeature[],
    setVenueOptions: (venues: AppFeature[]) => void,

    tourVenues: AppFeature[],
    addTourVenue: (venue: AppFeature) => void,
    removeTourVenue: (venue: AppFeature) => void
    setTourVenues: (venues: AppFeature[]) => void,

    tourOrigin: AppFeature|null,
    setTourOrigin: (venue: AppFeature|null) => void,
    tourDestination: AppFeature|null,
    setTourDestination: (venue: AppFeature|null) => void,

    calculateRoute: () => void,
    route: any,
    setRoute: (route: any) => void,
    isRouteStale: boolean,

    tourDates: any,
    setTourDates: (dates: {[k: string]: string}) => void,
    encodedPath: string,

    isShareModalOpen: boolean,
    setIsShareModalOpen: (b: boolean) => void,

    generateCSV: () => void,
    importHash: (s: string) => void
}

export const AppContext = React.createContext<AppContextProps>({
    tab: Tab.VENUES,
    setTab: () => {},

    mobileToggle: false,
    setMobileToggle: () => {},

    setMapViewport: () => {},

    venues: [],
    filteredVenues: [],

    filters: {venueType: [], capacityCategory: [], accessibilityArtist: false, accessibilityAudience: false},
    setFilters: () => {},

    selectedFilters: {venueType: [], capacityCategory: [], accessibilityArtist: false, accessibilityAudience: false},
    setSelectedFilters: () => {},

    showFilters: false,
    setShowFilters: () => {},
    
    filterCounter: 0,
    setFilterCounter: () => {},

    isFilterMapMove: false,
    setIsFilterMapMove: () => {},

    selectedVenue: null,
    setSelectedVenue: () => {},

    venueOptions: [],
    setVenueOptions: () => {},

    tourVenues: [],
    addTourVenue: () => {},
    removeTourVenue: () => {},
    setTourVenues: () => {},

    tourOrigin: null,
    setTourOrigin: () => {},
    tourDestination: null,
    setTourDestination: () => {},

    calculateRoute: () => {},
    route: null,
    setRoute: () => {},
    isRouteStale: true,

    tourDates: {},
    setTourDates: () => {},

    encodedPath: '',

    isShareModalOpen: false,
    setIsShareModalOpen: () => {},

    generateCSV: () => {},
    importHash: () => {}
});

export const AppContextController: React.FC = ({children}) => {
    const [venues] = useState<AppFeature[]>((window as any)?.rtnmap?.geojson?.features || []);
    const [tab, setTab] = useState<Tab>(Tab.VENUES);
    const [mobileToggle, setMobileToggle] = useState<boolean>(true);
    const [mapViewport, setMapViewport] = useState<MapViewport>();
    const debouncedMapViewport = useDebounce(mapViewport, 500);
    const [tourVenues, setTourVenues] = useState<AppFeature[]>([]);
    const [tourOrigin, setTourOrigin] = useState<AppFeature|null>(null);
    const [tourDestination, setTourDestination] = useState<AppFeature|null>(null);
    const [selectedVenue, setSelectedVenue] = useState<AppFeature|null>(null);
    const [venueOptions, setVenueOptions] = useState<AppFeature[]>([]);
    const [filters, setFilters] = useState<FilterType>({venueType: [], capacityCategory: [], accessibilityArtist: false, accessibilityAudience: false});
    const [selectedFilters, setSelectedFilters] = useState<FilterType>({venueType: [], capacityCategory: [], accessibilityArtist: false, accessibilityAudience: false});
    const [isFilterMapMove, setIsFilterMapMove] = useState<boolean>(false);
    const [tourDates, setTourDates] = useState<{[k: string]: string}>({});
    const [route, setRoute] = useState<any>(null);
    const [isRouteStale, setIsRouteStale] = useState<boolean>(true);
    const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [filterCounter, setFilterCounter] = useState<number>(0);

    useEffect(() => {
        if(selectedVenue){
            setMobileToggle(false)
        }
    }, [selectedVenue, setMobileToggle])

    const addTourVenue = (venue: AppFeature) => {
        if(tourVenues.indexOf(venue) === -1){
            const newTourVenues = tourVenues.concat([venue]);
            // console.log({newTourVenues})
            setTourVenues(newTourVenues);
        }
    }

    const removeTourVenue = (venue: AppFeature) => {
        if(tourVenues.indexOf(venue) > -1){
            const newTourVenues = tourVenues.slice();
            newTourVenues.splice(tourVenues.indexOf(venue), 1);
            setTourVenues(newTourVenues);
        }
    }

    const filterFunc = (venue: AppFeature, filters: FilterType, viewport?: MapViewport) => {
        if(viewport && !viewport.bounds.contains(venue.geometry.coordinates as [number, number])){
            return false;
        }

        for(const filterKey in filters){
            if(!FILTER_CONFIG[filterKey]){
                // unknown filter
                continue;
            }

            if(FILTER_CONFIG[filterKey].type === 'multiselect'){
                if((filters as any)[filterKey].length === 0){
                    continue;
                }

                const keys = FILTER_CONFIG[filterKey] ? FILTER_CONFIG[filterKey].keys : [filterKey];
                const values = keys.map(k => (venue.properties as any)[k]).filter(v => v !== undefined);

                if(values.length === 0){
                    return false;
                }

                if(!(filters as any)[filterKey].some((filter: any) => filter.values.some((v: string) => values.includes(v)))){
                    return false;
                }
            }

            if(FILTER_CONFIG[filterKey].type === 'checkbox'){
                const checked = !!(filters as any)[filterKey];
                const acceptedValues = checked ? (FILTER_CONFIG[filterKey].values || ['true']) : [];

                if(acceptedValues.length === 0){
                    continue;
                }

                const keys = FILTER_CONFIG[filterKey] ? FILTER_CONFIG[filterKey].keys : [filterKey];
                const values = keys.map(k => (venue.properties as any)[k]).filter(v => v !== undefined);
                
                if(values.length === 0){
                    return false;
                }

                if(!acceptedValues.some((v: any) => values.includes(v))){
                    return false;
                }
            }
        }

        return true;
    }

    const filteredVenues = useMemo(() => {
        const viewport = isFilterMapMove ? debouncedMapViewport : undefined;
        return venues.sort((a, b) => a.properties.title.localeCompare(b.properties.title)).filter(v => filterFunc(v, filters, viewport));
    }, [venues, debouncedMapViewport, isFilterMapMove, filters]);

    const calculateRoute = useCallback(async (newTourOrigin?, newTourVenues?, newTourDestination?) => {
        const accessToken = MAPBOX_API_KEY;
        const waypoints = [];
        if(newTourOrigin || tourOrigin){
            waypoints.push(newTourOrigin || tourOrigin);
        }
        waypoints.push(...(newTourVenues || tourVenues));
        if(newTourDestination || tourDestination){
            waypoints.push(newTourDestination || tourDestination);
        }
        const coordinates = waypoints.map(v => v?.geometry.coordinates.join(',')).join(';')
        const {data} = await axios.get(`https://api.mapbox.com/directions/v5/mapbox/driving/${coordinates}`, {
            params: {
                access_token: accessToken,
                overview: 'full'
            }
        });
        // console.log(data);
        if(data && data.routes && data.routes.length > 0){
            setRoute(data.routes[0])
            setIsRouteStale(false);
        }
    }, [tourOrigin, tourVenues, tourDestination]);
    
    // clear route if anything has changed
    useEffect(() => {
        // setRoute(null);
        setIsRouteStale(true);
    }, [tourOrigin, tourVenues, tourDestination]);

    const toLossyJson = (v: AppFeature) => {
        if(v.properties._path.startsWith('poi/')){
            return [v.properties._path, ...(v.properties._path.startsWith('poi/') ? [...v.geometry.coordinates, v.properties.title, v.properties.address] : [])]
        }else{
            return v.properties._path;
        }
    }
    // encode url
    const encodedPath = useMemo(() => {
        const o = tourOrigin ? toLossyJson(tourOrigin) : undefined;
        const wp = tourVenues.length > 0 ? tourVenues.map(v => toLossyJson(v)) : undefined;
        const d = tourDestination ? toLossyJson(tourDestination) : undefined;
        const td = tourVenues.length > 0 && Object.keys(tourDates).length > 0 ? tourVenues.map(v => tourDates[v.properties._path] || 0) : undefined;

        const obj = [o||0, wp||0, d||0, td||0];
        return o||wp||d||td ? `${window.location.href.replace(/#.*$/, '')}#!/tour/${encodeURIComponent(JSON.stringify(obj))}` : ''
    }, [tourOrigin, tourVenues, tourDestination, tourDates]);

    const [hash, updateHash] = useHash();
    const importHash = useCallback((hash) => {
        if(hash){
            // console.log(hash);
            try{
                const lossyJson = decodeURIComponent(hash.replace(/^#!\/tour\//, ''));
                if(!lossyJson.startsWith('[')){return;}
                const [o, wp, d, td] = JSON.parse(lossyJson);
                const toFeature = (lossy: string | [string, number?, number?, string?, string?]) => {
                    const _path = (typeof lossy === 'string' ? lossy : lossy[0]) as string;
                    const venue = venues.find(v => v.properties._path === _path);
                    if(venue){return venue;}

                    if(typeof lossy === 'string'){
                        return;
                    }

                    return {
                        type: "Feature",
                        geometry: {
                            type: "Point",
                            coordinates: lossy.slice(1, 3)
                        },
                        properties: {
                            _path: lossy[0],
                            title: lossy[3],
                            address: lossy[4]
                        }
                    } as AppFeature
                }

                let tourOrigin;
                let tourVenues;
                let tourDestination;
                let newTourDates: {[k: string]: string} = {};
                if(o){
                    tourOrigin = toFeature(o)
                }

                if(wp){
                    tourVenues = wp.map((v: any) => toFeature(v)).filter((v: any) => v);
                }

                if(d){
                    tourDestination = toFeature(d)
                }

                if(td != null && tourVenues){
                    for(let i = 0; i < td.length; i++){
                        if(td[i] && tourVenues[i]){
                            newTourDates[tourVenues[i].properties._path] = td[i];
                        }
                    }
                }

                if(tourOrigin){setTourOrigin(tourOrigin)}
                if(tourVenues){setTourVenues(tourVenues)}
                if(tourDestination){setTourDestination(tourDestination)}
                if(newTourDates){setTourDates(newTourDates)}

                setTab(1);

            }catch(e){
                // console.error(e)
            }
        }
    }, [venues])

    useEffect(() => {
        if(window.location.hash){
            importHash(window.location.hash);
        }
    }, [importHash]);

    useEffect(() => {
        if(!encodedPath){
            updateHash('#')
        }else{
            updateHash(encodedPath.replace(/^.*?#/, '#'));
        }
    }, [encodedPath, updateHash]);

    const generateCSV = useCallback(() => {
        const rows = [['DATE', 'VENUE_NAME', 'ADDRESS', 'PAX', 'CAPACITY_CATEGORY', 'ROOM_NAME', 'VENUE_TYPE', 'VENUE_PHONE', 'VENUE_EMAIL', 'WEBSITE', 'FACEBOOK', 'INSTAGRAM', 'BOOKING_NAME', 'BOOKING_EMAIL', 'BOOKING_PHONE', 'ENGINEER', 'ACCESSIBILITY_AUDIENCE', 'ACCESSIBILITY_ARTIST']];
        const toRow = (v: AppFeature) => {
            return [
                tourDates[v.properties._path] || '',
                v.properties.title,
                v.properties.address,
                v.properties.pax,
                v.properties.capacity_category,
                v.properties.room_name,
                v.properties.venue_type,
                v.properties.venue_phone,
                v.properties.venue_email,
                v.properties.website,
                v.properties.facebook,
                v.properties.instagram,
                v.properties.booking_name,
                v.properties.booking_email,
                v.properties.booking_phone,
                v.properties.engineer,
                v.properties.accessibility_audience,
                v.properties.accessibility_artist
            ];
        }
        if(tourOrigin){
            rows.push(toRow(tourOrigin))
        }
        if(tourVenues){
            rows.push(...tourVenues.map(v => toRow(v)))
        }
        if(tourDestination){
            rows.push(toRow(tourDestination))
        }

        const csv = stringify(rows, {})
        // console.log(csv);
        const encodedUri = encodeURI(csv);
        const link = document.createElement("a");
        link.setAttribute("href", "data:text/csv;charset=utf-8," + encodedUri);
        link.setAttribute("download", "route.csv");
        document.body.appendChild(link); // Required for FF

        link.click();
    }, [tourOrigin, tourVenues, tourDestination, tourDates]);

    return <AppContext.Provider value={{
        tab,
        setTab,
        mobileToggle,
        setMobileToggle,
        mapViewport,
        setMapViewport,
        venues,
        filteredVenues,
        tourVenues,
        selectedVenue,
        setSelectedVenue,
        venueOptions,
        setVenueOptions,
        addTourVenue,
        removeTourVenue,
        setTourVenues,
        filters,
        setFilters,
        isFilterMapMove,
        setIsFilterMapMove,
        tourOrigin,
        setTourOrigin,
        tourDestination,
        setTourDestination,
        calculateRoute,
        route,
        isRouteStale,
        setRoute,
        tourDates,
        setTourDates,
        encodedPath,
        isShareModalOpen,
        setIsShareModalOpen,
        generateCSV,
        importHash,

        showFilters, setShowFilters,
        filterCounter, setFilterCounter,
        selectedFilters, setSelectedFilters
    }}>
        {children}
    </AppContext.Provider>
}